// Prompt.jsx
// Rules:
// - In Library: user cannot Like or Copy their own prompt (prompt.userId === currentUserId).
// - In Collection: once published (isPublic === true), the Publish button becomes "Published" (disabled).
//
// Assumptions:
// - PromptLibrary has: userId (ObjectId/string) and likedBy[].
// - PromptUser has: isPublic (set true by /publish).
// - currentUserId comes from props.userId or localStorage.userId.

import React from "react";
import axios from "axios";
import {
  HandThumbUpIcon as HandThumbUpOutline,
  DocumentDuplicateIcon,
} from "@heroicons/react/24/outline";
import { HandThumbUpIcon as HandThumbUpSolid } from "@heroicons/react/24/solid";
import { WithContext as ReactTags } from "react-tag-input";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";



const API_BASE = process.env.REACT_APP_API_ENDPOINT || "";

const API = {
  collectionList: `${API_BASE}/promptUser/list`,
  collectionAdd: `${API_BASE}/promptUser`,
  collectionUpdate: (id) => `${API_BASE}/promptUser/${id}`,
  collectionDelete: (id) => `${API_BASE}/promptUser/${id}`,
  collectionPublish: (id) => `${API_BASE}/promptUser/${id}/publish`,
  libraryList: `${API_BASE}/promptLibrary/list`,
  libraryCopyToCollection: (id) => `${API_BASE}/promptLibrary/${id}/copy`,
  libraryToggleLike: (id) => `${API_BASE}/promptLibrary/${id}/like`,
   libraryDelete: (id) => `${API_BASE}/promptLibrary/${id}`
};

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return String(d);
    return dt.toLocaleDateString("en-AU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return String(d);
  }
};

const smartCap = (word) => {
  const upper = word.toUpperCase();
  const keepCaps = ["PTSD", "ADHD", "GP", "MHCP", "NDIS"];
  if (keepCaps.includes(upper)) return upper;
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};


// put near the top (outside the class)
const CATEGORIES = [
  "Consultation",
  "Letters",
  "Reports",
  "Care Plans",
  "Mental Health",
  "Chronic Disease",
  "Preventive Care",
  "Instruction", 
  "Referrals",
  "Admin",
  "Patient Education",
  "Other",
];


export default class Prompt extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab: "collection",
      loading: false,
      error: "",
      collection: [],
      library: [],
      selectedId: "",
      form: {
        title: "",
        description: "",
        promptText: "",
        category: "",
        tagsText: "",
      },
      saving: false,
      deleting: false,
      publishing: false,
      copying: false,
      liking: false,
      liked: false,
      tags: [],
      isEditing: false,
    originalForm: null,
    originalTags: [],

    };
  }

  componentDidMount() {
    this.loadActive();
  }

  currentUserId = () =>
    String(this.props.userId || localStorage.getItem("userId") || "");

  setTab = (tab) => {
    this.setState(
      {
        activeTab: tab,
        selectedId: "",
        error: "",
        liked: false,
        // ✅ reset right panel so collection shows placeholders on load
        form: { title: "", description: "", promptText: "", category: "" },
        tags: [],
        originalForm: null,
        originalTags: [],
        isEditing: false,
      },
      this.loadActive
    );
  };


  authHeaders = () => {
    const token = this.props.token || localStorage.getItem("token") || "";
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  axiosConfig = () => ({
    withCredentials: true,
    headers: { ...this.authHeaders() },
  });

  request = async (method, url, body) => {
    try {
      const config = this.axiosConfig();
      const res =
        method === "get" || method === "delete"
          ? await axios[method](url, config)
          : await axios[method](url, body, config);
      return res.data || {};
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Request failed";
      throw new Error(msg);
    }
  };

  loadActive = async () => {
    const { activeTab } = this.state;
    this.setState({ loading: true, error: "" });

    try {
      if (activeTab === "collection") {
        const data = await this.request("get", API.collectionList);
        const list = data.prompts || data.items || data.collection || [];
        this.setState({ collection: list });
      } else {
        const data = await this.request("get", API.libraryList);
        const list = data.prompts || data.items || data.library || [];
        this.setState({ library: list });
      }
    } catch (e) {
      this.setState({ error: e.message || "Failed to load." });
    } finally {
      this.setState({ loading: false });
    }
  };

  listForActive = () =>
    this.state.activeTab === "collection"
      ? this.state.collection
      : this.state.library;

  getSelected = () => {
    const list = this.listForActive();
    return list.find((x) => x._id === this.state.selectedId) || null;
  };

    isOwnLibraryPrompt = (item) => {
      const uid = String(this.currentUserId() || "");
      if (!uid || !item) return false;

      const raw = item.userId;

      // userId might be:
      // - "65f..." (string)
      // - ObjectId (object)
      // - populated object { _id: "65f...", ... }
      const itemUid =
        raw && typeof raw === "object"
          ? String(raw._id || raw.id || raw.toString?.() || "")
          : String(raw || "");

      return itemUid === uid;
    };



    selectItem = (item) => {
        const uid = this.currentUserId();
        const chips = Array.isArray(item?.tags) ? item.tags.map((t) => ({ id: t, text: t })) : [];

        const liked =
            this.state.activeTab === "library" &&
            Array.isArray(item?.likedBy) &&
            uid &&
            item.likedBy.some((u) => String(u) === uid);

        const nextForm = {
            title: item?.title || "",
            description: item?.description || "",
            promptText: item?.promptText || "",
            category: item?.category || "",
        };

        this.setState({
            selectedId: item?._id || "",
            liked: Boolean(liked),
            tags: chips,
            form: nextForm,
            originalForm: nextForm,
            originalTags: chips,
            isEditing: false, // ✅ lock on view
            error: "",
        });
    };


    newItem = () => {
        this.setState({
            selectedId: "",
            liked: false,
            tags: [],
            form: { title: "", description: "", promptText: "", category: "" },
            originalForm: null,
            originalTags: [],
            isEditing: true, // ✅ new prompt is editable
            error: "",
        });
    };

    startEdit = () => {
        this.setState({ isEditing: true });
    };

    cancelEdit = () => {
        this.setState((prev) => ({
            isEditing: false,
            form: prev.originalForm ? { ...prev.originalForm } : prev.form,
            tags: prev.originalTags ? [...prev.originalTags] : prev.tags,
            error: "",
        }));
    };

    confirmDelete = () => {
        const { selectedId } = this.state;
        if (!selectedId) return;

        confirmAlert({
            title: "Confirm delete",
            message: "Are you sure you want to delete this prompt?",
            buttons: [
            { label: "Yes, delete", onClick: () => this.deleteConfirmed() },
            { label: "Cancel" },
            ],
        });
    };

    deleteConfirmed = async () => {
        const { selectedId } = this.state;
        if (!selectedId) return;

        this.setState({ deleting: true, error: "" });
        try {
            await this.request("delete", API.collectionDelete(selectedId));
            this.setState((prev) => ({
            collection: prev.collection.filter((p) => p._id !== selectedId),
            selectedId: "",
            tags: [],
            form: { title: "", description: "", promptText: "", category: "" },
            originalForm: null,
            originalTags: [],
            isEditing: false,
            }));
        } catch (e) {
            this.setState({ error: e.message || "Delete failed." });
        } finally {
            this.setState({ deleting: false });
        }
    };


    handleChange = (field, val) => {
        this.setState((prev) => ({ form: { ...prev.form, [field]: val } }));
    };

    save = async () => {
        const { selectedId, form } = this.state;

        if (!form.title.trim() || !form.promptText.trim()) {
        this.setState({ error: "Title and prompt text are required." });
        return;
        }

        this.setState({ saving: true, error: "" });

        try {
        const payload = {
            title: form.title.trim(),
            description: form.description || "",
            promptText: form.promptText,
            category: form.category || "",
            tags: this.state.tags.map((t) => t.text),
        };

        if (!selectedId) {
            const data = await this.request("post", API.collectionAdd, payload);
            const created = data.prompt || data.userPrompt || data.item;

            this.setState((prev) => ({
            collection: created ? [created, ...prev.collection] : prev.collection,
            selectedId: created?._id || prev.selectedId,
            }));

            if (created) this.selectItem(created);
        } else {
            const data = await this.request(
            "put",
            API.collectionUpdate(selectedId),
            payload
            );
            const updated = data.prompt || data.userPrompt || data.item;

            this.setState((prev) => ({
            collection: prev.collection.map((p) =>
                p._id === selectedId ? updated || p : p
            ),
            }));

            if (updated) this.selectItem(updated);
        }
        } catch (e) {
        this.setState({ error: e.message || "Save failed." });
        } finally {
        this.setState({ saving: false });
        }
    };

    delete = async () => {
        const { selectedId } = this.state;
        if (!selectedId) return;

        this.setState({ deleting: true, error: "" });

        try {
        await this.request("delete", API.collectionDelete(selectedId));
        this.setState((prev) => ({
            collection: prev.collection.filter((p) => p._id !== selectedId),
            selectedId: "",
            form: {
            title: "",
            description: "",
            promptText: "",
            category: "",
            tagsText: "",
            },
        }));
        } catch (e) {
        this.setState({ error: e.message || "Delete failed." });
        } finally {
        this.setState({ deleting: false });
        }
    };

    publishSelected = async () => {
        const { selectedId } = this.state;
        if (!selectedId) return;

        this.setState({ publishing: true, error: "" });

        try {
        await this.request("post", API.collectionPublish(selectedId), {});
        this.setState((prev) => ({
            collection: prev.collection.map((p) =>
            p._id === selectedId ? { ...p, isPublic: true } : p
            ),
        }));
        } catch (e) {
        this.setState({ error: e.message || "Publish failed." });
        } finally {
        this.setState({ publishing: false });
        }
    };

  copySelectedToCollection = async () => {
    const sel = this.getSelected();
    if (!sel) return;
    if (this.isOwnLibraryPrompt(sel)) return;

    this.setState({ copying: true, error: "" });

    try {
      const data = await this.request(
        "post",
        API.libraryCopyToCollection(sel._id),
        {}
      );
      const userPrompt = data.userPrompt;
      if (userPrompt) {
        this.setState((prev) => ({
          collection: [userPrompt, ...prev.collection],
        }));
      }
    } catch (e) {
      this.setState({ error: e.message || "Copy failed." });
    } finally {
      this.setState({ copying: false });
    }
  };

  toggleLike = async () => {
    const sel = this.getSelected();
    if (!sel) return;
    if (this.isOwnLibraryPrompt(sel)) return;

    this.setState({ liking: true, error: "" });

    try {
      const data = await this.request("post", API.libraryToggleLike(sel._id), {});
      const updated = data.prompt;
      const likedNow =
        typeof data.liked === "boolean" ? data.liked : !this.state.liked;

      this.setState((prev) => ({
        liked: likedNow,
        library: updated
          ? prev.library.map((p) => (p._id === sel._id ? updated : p))
          : prev.library,
      }));
    } catch (e) {
      this.setState({ error: e.message || "Like failed." });
    } finally {
      this.setState({ liking: false });
    }
  };

    handleTagDelete = (i) => {
        this.setState((prev) => ({ tags: prev.tags.filter((_, idx) => idx !== i) }));
    };

    handleTagAdd = (tag) => {
        let text = String(tag?.text || "").trim();
        if (!text) return;

        text = smartCap(text);

        this.setState((prev) => {
            const exists = prev.tags.some(
            (t) => t.text.toLowerCase() === text.toLowerCase()
            );
            if (exists) return prev;

            return { tags: [...prev.tags, { id: text, text }] };
        });
    };

    handleTagDrag = (tag, currPos, newPos) => {
        this.setState((prev) => {
            const tags = [...prev.tags];
            tags.splice(currPos, 1);
            tags.splice(newPos, 0, tag);
            return { tags };
        });
    };
    getTagSuggestions = () => {
        const uniq = new Map();

        const addFrom = (arr) => {
            (arr || []).forEach((p) => {
            (p?.tags || []).forEach((t) => {
                const text = String(t || "").trim();
                if (!text) return;
                const key = text.toLowerCase();
                if (!uniq.has(key)) uniq.set(key, { id: text, text });
            });
            });
        };

        addFrom(this.state.collection);
        addFrom(this.state.library);

        return Array.from(uniq.values()).sort((a, b) => a.text.localeCompare(b.text));
    };

    confirmDeleteLibrary = (id) => {
      if (!id) return;

      confirmAlert({
        title: "Confirm delete",
        message: "Are you sure you want to delete this library prompt?",
        buttons: [
          { label: "Yes, delete", onClick: () => this.deleteLibraryConfirmed(id) },
          { label: "Cancel" },
        ],
      });
    };

    deleteLibraryConfirmed = async (id) => {
      if (!id) return;

      this.setState({ deleting: true, error: "" });

      try {
        await this.request("delete", API.libraryDelete(id));

        this.setState((prev) => ({
          library: prev.library.filter((p) => p._id !== id),
          selectedId: prev.selectedId === id ? "" : prev.selectedId,
        }));

        // if we deleted the selected item, clear the right panel too
        if (this.state.selectedId === id) {
          this.setState({
            form: { title: "", description: "", promptText: "", category: "" },
            tags: [],
            originalForm: null,
            originalTags: [],
            isEditing: false,
          });
        }
      } catch (e) {
        this.setState({ error: e.message || "Delete failed." });
      } finally {
        this.setState({ deleting: false });
      }
    };


  renderTabs() {
    const { activeTab } = this.state;

    const tabStyle = (tab) =>
      `px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
        activeTab === tab
          ? "bg-white text-sky-700 shadow-sm ring-1 ring-slate-200"
          : "text-slate-500 hover:text-slate-800 hover:bg-white/70"
      }`;

    return (
      <div className="mt-4">
        <div className="inline-flex items-center gap-1 rounded-2xl bg-slate-100 p-1 shadow-inner">
          <button
            className={tabStyle("collection")}
            onClick={() => this.setTab("collection")}
          >
            Collection
          </button>

          <button
            className={tabStyle("library")}
            onClick={() => this.setTab("library")}
          >
            Library
          </button>
        </div>
      </div>
    );
  }

  renderListItem(item) {
    const selected = this.state.selectedId === item._id;
    const isLibrary = this.state.activeTab === "library";
    const isOwnLibrary = isLibrary && this.isOwnLibraryPrompt(item);

    return (
      <div
        key={item._id}
        onClick={() => this.selectItem(item)}
        className={[
          "group cursor-pointer rounded-2xl border p-4 mb-3 shadow-sm transition",
          selected
            ? "border-sky-300 bg-sky-50 ring-1 ring-sky-200 shadow-md"
            : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-md",
        ].join(" ")}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="text-xs text-slate-500">
            {fmtDate(item.createdAt || item.updatedAt)}
          </div>

          {this.state.activeTab === "library" ? (
            <div className="flex items-center gap-2">
              <div className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-600">
                ❤️ {item.likes || 0} · 📄 {item.copies || 0}
              </div>

              {isOwnLibrary && (
                <button
                  className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:bg-red-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    this.confirmDeleteLibrary(item._id);
                  }}
                  title="Delete this library prompt"
                >
                  Delete
                </button>
              )}
            </div>
          ) : (
            <div className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-600">
              {item.isCopied ? "Copied" : "Manual"}
            </div>
          )}
        </div>

        <div className="mt-3 text-lg font-bold text-slate-800">
          {item.title || "Untitled"}
        </div>

        <div className="mt-1 text-sm text-slate-600 line-clamp-2 leading-6">
          {item.description || "—"}
        </div>

        {Array.isArray(item.tags) && item.tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {item.tags.slice(0, 4).map((tag, idx) => (
              <span
                key={`${tag}-${idx}`}
                className="rounded-full bg-cyan-50 px-2.5 py-1 text-[11px] font-medium text-cyan-700"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  renderRightPanel() {
    const {
      activeTab,
      selectedId,
      form,
      saving,
      deleting,
      publishing,
      copying,
      liking,
      liked,
      isEditing,
    } = this.state;

    const btnBase =
      "px-4 py-2.5 rounded-2xl text-sm font-semibold border transition inline-flex items-center gap-2";
    const btnPrimary =
      "bg-gradient-to-r from-sky-700 to-cyan-700 text-white border-sky-700 hover:from-sky-800 hover:to-cyan-800 shadow-md";
    const btnGhost =
      "bg-white text-slate-800 border-slate-200 hover:bg-slate-100 shadow-sm";
    const btnDanger =
      "bg-white text-red-600 border-red-200 hover:bg-red-50 shadow-sm";
    const disabled = "opacity-50 cursor-not-allowed";

    const LikeIcon = liked ? HandThumbUpSolid : HandThumbUpOutline;

    const selectedCollection =
      activeTab === "collection" ? this.getSelected() : null;
    const isPublished = Boolean(selectedCollection?.isPublic);

    const selectedLibrary =
      activeTab === "library" ? this.getSelected() : null;
    const isOwn =
      activeTab === "library" ? this.isOwnLibraryPrompt(selectedLibrary) : false;

    const tagSuggestions = this.getTagSuggestions();

    const viewingExisting =
      Boolean(selectedId) && activeTab === "collection" && !isEditing;

    const blankCollectionView =
      activeTab === "collection" && !selectedId && !isEditing;

    const inputsDisabled =
      activeTab === "library" || viewingExisting || blankCollectionView;

    return (
      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-lg">
        <div className="bg-gradient-to-r from-sky-900 via-sky-800 to-cyan-700 px-5 py-4 text-white">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-xl font-bold tracking-tight">
                {activeTab === "library"
                  ? "Library Prompt"
                  : selectedId
                  ? "Prompt Details"
                  : "New Prompt"}
              </div>
              <div className="mt-1 text-sm text-white/80">
                {activeTab === "library"
                  ? selectedLibrary
                    ? `${fmtDate(selectedLibrary.createdAt || selectedLibrary.updatedAt)} · ${
                        (selectedLibrary.author || "").trim() || "Unknown"
                      }`
                    : "Select a prompt from the library."
                  : selectedId
                  ? "Review or update your selected prompt."
                  : "Create a new prompt for your collection."}
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {activeTab === "collection" &&
                (isPublished ? (
                  <button className={`${btnBase} ${btnGhost} ${disabled}`} disabled>
                    Published
                  </button>
                ) : (
                  <button
                    className={`${btnBase} ${btnGhost} ${
                      !selectedId || publishing ? disabled : ""
                    }`}
                    disabled={!selectedId || publishing}
                    onClick={this.publishSelected}
                    title="Publish this prompt to the community library"
                  >
                    {publishing ? "Publishing..." : "Publish"}
                  </button>
                ))}

              {activeTab === "library" && (
                <>
                  <button
                    className={`${btnBase} ${btnGhost} ${
                      !selectedId || liking || isOwn ? disabled : ""
                    }`}
                    disabled={!selectedId || liking || isOwn}
                    onClick={this.toggleLike}
                    title={
                      isOwn
                        ? "You can't like your own prompt"
                        : "Like this prompt"
                    }
                  >
                    <LikeIcon
                      className={
                        liked ? "h-5 w-5 text-yellow-500" : "h-5 w-5 text-slate-500"
                      }
                    />
                    <span>{liked ? "Liked" : "Like"}</span>
                  </button>

                  <button
                    className={`${btnBase} ${btnGhost} ${
                      !selectedId || copying || isOwn ? disabled : ""
                    }`}
                    disabled={!selectedId || copying || isOwn}
                    onClick={this.copySelectedToCollection}
                    title={
                      isOwn
                        ? "You can't copy your own prompt"
                        : "Copy to your collection"
                    }
                  >
                    <DocumentDuplicateIcon className="h-5 w-5 text-slate-500" />
                    <span>{copying ? "Copying..." : "Copy"}</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="p-5">
          <div className="grid gap-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Title
              </div>
              <input
                className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-800 shadow-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-200 disabled:bg-slate-100"
                value={form.title}
                onChange={(e) => this.handleChange("title", e.target.value)}
                placeholder="e.g. MHCP generator from transcript"
                disabled={inputsDisabled}
              />
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Description
              </div>
              <textarea
                className="w-full min-h-[90px] rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-800 shadow-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-200 disabled:bg-slate-100"
                value={form.description}
                onChange={(e) => this.handleChange("description", e.target.value)}
                placeholder="Short description..."
                disabled={inputsDisabled}
              />
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Prompt Text
              </div>
              <textarea
                className="w-full min-h-[260px] rounded-2xl border border-slate-300 bg-white px-4 py-3 font-mono text-sm text-slate-800 shadow-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-200 disabled:bg-slate-100"
                value={form.promptText}
                onChange={(e) => this.handleChange("promptText", e.target.value)}
                placeholder="Paste your prompt here..."
                disabled={inputsDisabled}
              />
            </div>

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-1">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Category
                </div>
                <select
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-800 shadow-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-200 disabled:bg-slate-100"
                  value={form.category || ""}
                  onChange={(e) => this.handleChange("category", e.target.value)}
                  disabled={inputsDisabled}
                >
                  <option value="">Select category…</option>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Tags
                </div>
                <ReactTags
                  tags={this.state.tags}
                  suggestions={tagSuggestions}
                  delimiters={[188, 13]}
                  handleDelete={this.handleTagDelete}
                  handleAddition={this.handleTagAdd}
                  handleDrag={this.handleTagDrag}
                  inputFieldPosition="top"
                  placeholder="Type a tag and press Enter"
                  readOnly={inputsDisabled}
                  autocomplete
                  classNames={{
                    tags: "flex flex-wrap gap-2",
                    tagInput: "w-full",
                    tagInputField:
                      "w-full px-4 py-3 border border-slate-300 rounded-2xl bg-white shadow-sm focus:ring-2 focus:ring-sky-200 focus:border-sky-400 disabled:bg-slate-100",
                    selected: "flex flex-wrap gap-2 mt-3",
                    tag:
                      "bg-sky-700 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-2",
                    remove: "cursor-pointer opacity-70 hover:opacity-100",
                    suggestions:
                      "bg-white border border-slate-200 rounded-2xl shadow-xl mt-2 overflow-hidden",
                    activeSuggestion: "bg-sky-50",
                  }}
                />
              </div>
            </div>

            {activeTab === "collection" ? (
              <div className="flex flex-wrap items-center gap-3 pt-2">
                {!selectedId ? (
                  <>
                    <button
                      className={`${btnBase} ${btnPrimary} ${saving ? disabled : ""}`}
                      disabled={saving}
                      onClick={this.save}
                    >
                      {saving ? "Saving..." : "Save"}
                    </button>

                    <button className={`${btnBase} ${btnGhost}`} onClick={this.newItem}>
                      New
                    </button>
                  </>
                ) : !isEditing ? (
                  <>
                    <button className={`${btnBase} ${btnPrimary}`} onClick={this.startEdit}>
                      Update
                    </button>

                    <button
                      className={`${btnBase} ${btnDanger} ${deleting ? disabled : ""}`}
                      disabled={deleting}
                      onClick={this.confirmDelete}
                    >
                      {deleting ? "Deleting..." : "Delete"}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className={`${btnBase} ${btnPrimary} ${saving ? disabled : ""}`}
                      disabled={saving}
                      onClick={this.save}
                    >
                      {saving ? "Saving..." : "Save"}
                    </button>

                    <button className={`${btnBase} ${btnDanger}`} onClick={this.cancelEdit}>
                      Cancel
                    </button>
                  </>
                )}
              </div>
            ) : (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-500">
                {isOwn
                  ? "This is your prompt. You can't like or copy your own library prompt."
                  : "Library prompts can be liked or copied into your collection."}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }


  render() {
      const { activeTab, loading, error } = this.state;
      const list = this.listForActive();

      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-sky-50/40 p-6">
          <div className="mx-auto max-w-7xl">
            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl">
              <div className="bg-gradient-to-r from-slate-900 via-sky-900 to-cyan-800 px-6 py-8 text-white">
                <div className="max-w-3xl">
                  <div className="text-3xl font-bold tracking-tight">Prompt Library</div>
                  <p className="mt-2 text-sm text-white/80">
                    Build your own reusable prompt collection or browse the community library.
                  </p>
                  {this.renderTabs()}
                </div>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-2 gap-5 sm:grid-cols-1 xs:grid-cols-1">
                  {/* Left panel */}
                  <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-lg">
                    <div className="border-b border-slate-200 bg-slate-50 px-5 py-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <div className="text-lg font-bold text-slate-900">
                            {activeTab === "collection"
                              ? "My Collection"
                              : "Community Library"}
                          </div>
                          <div className="mt-1 text-sm text-slate-500">
                            {activeTab === "collection"
                              ? "Create and manage your own saved prompts."
                              : "Browse prompts shared by the community."}
                          </div>
                        </div>

                        <div className="flex gap-2">
                          {activeTab === "collection" && (
                            <button
                              className="rounded-2xl bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm ring-1 ring-slate-200 transition hover:bg-slate-100"
                              onClick={this.newItem}
                            >
                              Add New
                            </button>
                          )}

                          <button
                            className="rounded-2xl bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm ring-1 ring-slate-200 transition hover:bg-slate-100"
                            onClick={this.loadActive}
                          >
                            Refresh
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="p-4">
                      {loading ? (
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-600">
                          Loading...
                        </div>
                      ) : (
                        <div>
                          {list.length === 0 ? (
                            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-600">
                              No prompts yet.
                            </div>
                          ) : (
                            list.map((item) => this.renderListItem(item))
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right panel */}
                  <div>
                    {error ? (
                      <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 shadow-sm">
                        {error}
                      </div>
                    ) : null}

                    {this.renderRightPanel()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
}
